import React, { useState, useRef, useEffect } from "react";
import api, { API } from "@/api";
import { MessageCircle, X, Send, Sparkles, Trash2 } from "lucide-react";

const SUGGESTIONS = [
  "Am I too concentrated in one stock?",
  "What should I buy to hit $500/month in dividends?",
  "Which of my holdings has the highest dividend cut risk?",
  "Compare my portfolio yield vs the S&P 500.",
];

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, streaming]);

  // Load history the first time chat is opened
  useEffect(() => {
    if (!open || historyLoaded) return;
    (async () => {
      try {
        const { data } = await api.get("/ai/chat/history");
        const past = (data.messages || []).map((m) => ({ role: m.role, content: m.content }));
        setMessages(past);
      } catch (e) {
        console.error("Failed to load chat history", e);
      } finally {
        setHistoryLoaded(true);
      }
    })();
  }, [open, historyLoaded]);

  const clearHistory = async () => {
    if (streaming) return;
    try {
      await api.delete("/ai/chat/history");
      setMessages([]);
    } catch (e) {
      console.error("Clear history failed", e);
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: `Sorry — I couldn't reach the AI service. (${e.message})\n\nIf you're seeing "budget exceeded", please top up your Emergent Universal Key balance in your profile.`,
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          data-testid="ai-chat-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3"
          style={{
            background: "var(--gain)",
            color: "#000",
            fontFamily: "JetBrains Mono, monospace",
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            boxShadow: "0 8px 24px rgba(0, 230, 118, 0.3)",
          }}
        >
          <Sparkles size={14} /> Ask AI
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[400px] max-w-[calc(100vw-3rem)] flex flex-col fade-up"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderTop: "3px solid var(--gain)",
            height: "min(600px, calc(100vh - 3rem))",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2">
              <MessageCircle size={14} style={{ color: "var(--gain)" }} />
              <div>
                <p className="text-xs tracking-widest uppercase">AI Advisor</p>
                <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                  Claude Sonnet 4.5 · knows your portfolio
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  data-testid="ai-chat-clear"
                  className="btn-ghost"
                  onClick={clearHistory}
                  disabled={streaming}
                  title="Clear chat history"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <button data-testid="ai-chat-close" className="btn-ghost" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Ask anything about your portfolio. Try:
                </p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`ai-suggest-${i}`}
                    className="w-full text-left text-xs p-3 border transition-colors hover:bg-slate-800"
                    style={{ borderColor: "var(--border)" }}
                    onClick={() => send(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`text-xs leading-relaxed ${m.role === "user" ? "text-right" : ""}`}
                data-testid={`ai-msg-${m.role}-${i}`}
              >
                {m.role === "user" ? (
                  <span
                    className="inline-block px-3 py-2"
                    style={{ background: "var(--gain-bg)", color: "var(--text-primary)", maxWidth: "85%" }}
                  >
                    {m.content}
                  </span>
                ) : (
                  <div
                    className="px-3 py-2 whitespace-pre-wrap"
                    style={{ background: "var(--bg)", border: "1px solid var(--border)" }}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <span className="blink">█</span> : null)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="flex gap-2">
              <input
                data-testid="ai-chat-input"
                className="input-base !py-2 !text-xs"
                placeholder={streaming ? "Claude is thinking..." : "Ask about your portfolio"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !streaming && send()}
                disabled={streaming}
              />
              <button
                data-testid="ai-chat-send"
                className="btn-primary !px-3 !py-2 disabled:opacity-40"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
              >
                <Send size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
